import React from 'react';

import { IComponent } from '../../../typings/generic-component';
import { makeClassNames } from '../../../utils/styles';
import ArticleAbstract from '../../molecules/article-abstract/article-abstract';

import { HTMLTextTags, IArticleElementDetail, tagsType } from './article-builder-tags';

import './article-builder.scss';

/* Import react-dom/server optional Dep and fallback*/
try {
  // @ts-ignore
  const ReactDOMServer = require('react-dom/server');
} catch (error) {
  // eslint-disable-next-line no-console
  console.warn(
    'ArticleBuilder require react-dom/server to work: please install this dependency running: npm install react-dom '
  );
}

export interface IArticleBuilder extends IComponent {
  elements: IArticleElements;
  template: string;
}

export interface IHTMLElementProps {
  href?: string;
  className?: string;
  customProps?: any;
}

interface IArticleElements {
  [key: string]: IArticleElementDetail;
}

const getAllowedTags = (): { [key: string]: any } => {
  return {
    ...HTMLTextTags,
    articleAbstract: ArticleAbstract, // Complex abstract component to render
  };
};

/**
 * Build classnames object
 * @returns prefixed classnames
 */
const classNames = () =>
  makeClassNames({
    ArticleBuilder: 'article-builder',
    ArticleBuilderContent: 'article-builder-content',
  });

/**
 * Build class starting from tag
 * @param tag tag element
 * @param cn classnames
 * @returns builded classname
 */
const buildDefaultClassesByTag = (tag?: tagsType, /* istanbul ignore next */ cn = classNames()) =>
  `${cn.ArticleBuilderContent}--${tag}`;

const customClassNames = (className?: string) =>
  makeClassNames({
    setClassName: className,
  });

/**
 * Build classes
 * @param hasValidTag bool on valid tag
 * @param tag tag string
 * @param className base classname
 * @returns builded classnames
 */
const buildClassName = (hasValidTag: boolean, tag?: tagsType, className?: string) => {
  const cn = customClassNames(className);
  if (className) {
    return cn.setClassName;
  } else if (hasValidTag) {
    return buildDefaultClassesByTag(tag);
  } else {
    return undefined;
  }
};

/**
 * Build HTML element starting from react Element
 * @param reactElement the react element
 * @returns Html string element
 */
const buildHTMLElement = (reactElement: React.ComponentElement<any, React.Component<any, any, any>>): string => {
  try {
    // @ts-ignore
    return ReactDOMServer?.renderToStaticMarkup(reactElement) || '';
  } catch (error) {
    // Because of react-dom/server is an optional dependency we need to catch the error.
    // eslint-disable-next-line no-console
    console.error(error);
    return '';
  }
};
/**
 * Check if elements object is empty
 * @param obj IArticleElements object
 * @returns boolean check on empty objects
 */
function isEmptyElementsObj(obj: IArticleElements): boolean {
  return Object.keys(obj).length === 0;
}

/**
 * Build a placehoder key
 * @param key the base key
 * @returns string key with placeholder params
 */
const buildPKey = (key: string): string => `<% ${key} %>`;

/**
 * Check if the base article tables has a specific key to be replaced with data
 * @param key the key to check
 * @param template base article template
 * @returns blool on check
 */
const templateHasKey = (key: string, template: string): boolean => template.includes(buildPKey(key));

/**
 * Replace all occourences in the template article with builder object
 * @param template base article template
 * @param mapObj builder objects for placeholders replacement
 * @returns filled article without pkeys
 */
const replaceAll = (template: string, mapObj: any): string => {
  const re = new RegExp(Object.keys(mapObj).join('|'), 'gi');
  return template.replace(re, (matched) => {
    return mapObj[matched];
  });
};

/**
 * Utility function to build React.createElement props
 * @param param0 props to build
 * @returns props object
 */
const propsBuilder = ({ href, className, customProps }: IHTMLElementProps) => {
  return { href, className, ...customProps };
};

/**
 * Fill article template starting from elementsMap and template
 * @param elementsMap elements map
 * @param template template to fill
 * @returns filled article
 */
const fillArticleTemplate = (elementsMap: Map<string, string>, template: string): string =>
  replaceAll(template, Object.fromEntries(elementsMap));

/**
 * Create element staring from element detail
 * @param elementDetail details to create element
 * @returns React Component && HTMLElement created
 */
const createElement = (elementDetail: IArticleElementDetail, key: string) => {
  const { tag, text, link, className, customProps } = elementDetail;

  const allowedTags = getAllowedTags();
  const hasValidTag = link ? true : Boolean(allowedTags[tag || '']);
  if (hasValidTag || !tag) {
    const tagToCreate = link ? allowedTags.a : allowedTags[tag || ''] || React.Fragment;
    const elementProps = hasValidTag
      ? propsBuilder({ href: link, className: buildClassName(hasValidTag, tagToCreate, className), customProps })
      : {};
    const reactElement = React.createElement(tagToCreate, elementProps, `${text || ''}`);
    const htmlElement = buildHTMLElement(reactElement);
    return {
      reactElement,
      htmlElement,
    };
  } else {
    const pkey = buildPKey(key || '');
    // eslint-disable-next-line no-console
    // console.error(`Article builder ERROR: tag: ${tag} not valid - for ${pkey} element in template `);
    return {
      reactElement: null,
      htmlElement: `*WARNING: [TAG:${tag} not valid for ${pkey} element in template]*`,
    };
  }
};

/**
 * Build article function
 * @param elements Array of elements to build and replace
 * @param template article template to fill (with placeholders)
 * @returns filled article without placeholder elements
 */
const buildArticle = (elements: IArticleElements, template: string) => {
  const articleComponents = new Map<string, string>();
  // eslint-disable-next-line guard-for-in
  for (const key in elements) {
    /* istanbul ignore else */ if (templateHasKey(key, template)) {
      const { htmlElement } = createElement(elements[key], key);
      const pkey = buildPKey(key);
      articleComponents.set(pkey, htmlElement);
    }
  }

  return fillArticleTemplate(articleComponents, template);
};

/**
 * Article builder
 * placeholder key = <% key %>
 */
const ArticleBuilder = ({ elements = {}, template = '' }: IArticleBuilder) => {
  const cn = classNames();
  const article = isEmptyElementsObj(elements) ? template : buildArticle(elements, template);
  return (
    <article className={cn.ArticleBuilder}>
      <div className={cn.ArticleBuilderContent} dangerouslySetInnerHTML={{ __html: article }} />
    </article>
  );
};

export default ArticleBuilder;
